import { useCallback, useRef } from 'react';
import * as contentful from 'contentful';
import { ContentfulClientApi } from 'contentful';
import { StaticContentApiService } from '../../../domain/static-content/static-content-api.service';
import { getStihlApps } from '../helpers/contentful.client';

export const useContentful = () => {
  const contentfulClient = useRef<null | ContentfulClientApi>(null);

  const formatLocale = (locale: string): string => {
    const parts = locale.split('-');
    if (parts.length !== 2) {
      throw new Error('Locale must be in the format of language-country (e.g., en-US)');
    }
    const [language, country] = parts;
    if (language.length !== 2 || country.length !== 2) {
      throw new Error('Both language and country codes must be two characters long');
    }
    return `${language.toLowerCase()}-${country.toUpperCase()}`;
  };

  const getStihlIdMenuNavItems = useCallback(
    async (locale = 'en-US') => {
      const formattedLocale = formatLocale(locale);
      const client = initContentfulClient();

      const links = await client.getEntries({
        content_type: 'links',
        locale: formattedLocale,
        'fields.scope': 'global_stihlIdProfile',
        order: 'fields.sortOrder',
      });
      return links.items.map((item) => item.fields).map(StaticContentApiService.mapToGlobalLink) || [];
    },
    [contentfulClient.current]
  );

  const getStihlLink = useCallback(
    async (linkId: string, locale = 'en-US') => {
      const client = initContentfulClient();

      const links = await client.getEntries({
        content_type: 'links',
        locale,
        order: 'fields.sortOrder',
      });

      const allLinks = links.items.map((item) => item.fields).map(StaticContentApiService.mapToGlobalLink) || [];
      return allLinks.find((link) => link.id === linkId) || null;
    },
    [contentfulClient.current]
  );

  const getAppSwitcherNavItems = async (locale = 'en-US') => getStihlApps(locale);

  const getAppSwitcherMetaNavItems = useCallback(
    async (locale = 'en-US') => {
      const formattedLocale = formatLocale(locale);
      const client = initContentfulClient();

      const links = await client.getEntries({
        content_type: 'links',
        locale: formattedLocale,
        'fields.scope': 'app_switcher',
      });
      return links.items.map((item) => item.fields).map(StaticContentApiService.mapToGlobalLink) || [];
    },
    [contentfulClient.current]
  );

  const initContentfulClient = useCallback(() => {
    if (!contentfulClient.current) {
      contentfulClient.current = contentful.createClient({
        space: process.env.NEXT_PUBLIC_CONTENTFUL_SPACE as string,
        environment: process.env.NEXT_PUBLIC_CONTENTFUL_ENV as string,
        accessToken: process.env.NEXT_PUBLIC_NAVIGATION_CONTENTFUL_API_TOKEN as string,
      });
    }
    return contentfulClient.current;
  }, []);

  return {
    getStihlLink,
    getStihlIdMenuNavItems,
    getAppSwitcherNavItems,
    getAppSwitcherMetaNavItems,
  };
};
